import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { Settings, FlaskConical, Eye, EyeOff, Copy, RefreshCw, Key, Phone, PhoneCall, Code, AlertCircle, CheckCircle, ChevronRight, Zap, FileText, Sparkles, ChevronDown } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';

const OutboundApiManager = () => {
  const [activeTab, setActiveTab] = useState('settings');
  const [apiSecret, setApiSecret] = useState('');
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [agents, setAgents] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [testCall, setTestCall] = useState({
    from_number: '',
    to_number: '',
    variables: {},
    overrides: {},
    metadata: {}
  });
  
  // Add state for raw JSON strings to allow editing
  const [variablesJson, setVariablesJson] = useState('{}');
  const [overridesJson, setOverridesJson] = useState('{}');
  
  // Dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    loadAgents();
    loadPhoneNumbers();
    loadApiSecret();
    
    // Initialize JSON strings
    setVariablesJson(JSON.stringify(testCall.variables, null, 2));
    setOverridesJson(JSON.stringify(testCall.overrides, null, 2));
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);


  const loadAgents = async () => {
    try {
      const response = await api.get('/agents');
      setAgents(response.data || []);
    } catch (err) {
      console.error('Failed to load agents:', err);
    }
  };

  const loadPhoneNumbers = async () => {
    try {
      const response = await api.get('/numbers');
      setPhoneNumbers(response.data || []);
    } catch (err) {
      console.error('Failed to load phone numbers:', err);
    }
  };

  const loadApiSecret = async () => {
    try {
      const response = await api.get('/outbound/api-secret');
      setApiSecret(response.data.secret || '');
    } catch (err) {
      console.error('Failed to load API secret:', err);
      setError('Failed to load API secret');
    }
  };

  const rotateApiSecret = async () => {
    try {
      setLoading(true);
      const response = await api.post('/outbound/rotate-secret');
      setApiSecret(response.data.secret);
      setSuccess('API secret rotated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to rotate API secret:', err);
      setError('Failed to rotate API secret');
    } finally {
      setLoading(false);
    }
  };

  const testOutboundCall = async () => {
    try {
      setLoading(true);
      
      // Parse JSON from textarea fields right before making the call
      let variables = {};
      let overrides = {};
      
      try {
        variables = JSON.parse(variablesJson);
      } catch (err) {
        console.error('Invalid variables JSON:', err);
        setError('Invalid variables JSON format');
        setLoading(false);
        return;
      }
      
      try {
        overrides = JSON.parse(overridesJson);
      } catch (err) {
        console.error('Invalid overrides JSON:', err);
        setError('Invalid overrides JSON format');
        setLoading(false);
        return;
      }
      
      // Create the call request with parsed JSON
      const callRequest = {
        ...testCall,
        variables: variables,
        overrides: overrides
      };
      
      const response = await api.post('/outbound/test-call', callRequest);
      setSuccess(`Test call created: ${response.data.message}`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Failed to create test call:', err);
      setError(err.response?.data?.detail || 'Failed to create test call');
    } finally {
      setLoading(false);
    }
  };

  const generateHmacSignature = (payload, secret, timestamp) => {
    // This would be implemented in a real application
    // For demo purposes, we'll show the structure
    return `sha256=${btoa(secret + payload + timestamp)}`;
  };

  const generateTestRequest = () => {
    const timestamp = Date.now();
    const payload = JSON.stringify(testCall);
    const signature = generateHmacSignature(payload, apiSecret, timestamp);
    
    return {
      url: `${window.location.origin}/v1/outbound/calls`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-BrainCX-Signature': signature,
        'X-BrainCX-Timestamp': timestamp.toString(),
        'X-Idempotency-Key': `test-${Date.now()}`
      },
      body: payload
    };
  };

  // Helper functions for JSON editing
  const formatJson = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch (err) {
      return jsonString; // Return original if invalid
    }
  };

  const setExampleVariables = () => {
    const example = {
      "first_name": "John",
      "last_name": "Smith", 
      "appointment_time": "2:00 PM tomorrow",
      "appointment_type": "consultation",
      "location": "downtown office",
      "agent_name": "Sarah",
      "company_name": "BrainCX"
    };
    setVariablesJson(JSON.stringify(example, null, 2));
    setTestCall({...testCall, variables: example});
  };

  const setExampleOverrides = () => {
    const example = {
      "system_prompt": "You are calling {{first_name}} about their {{appointment_type}} appointment at {{appointment_time}} in our {{location}}. Be professional and confirm their attendance. Use their name {{first_name}} throughout the call.",
      "greeting_text": "Hello {{first_name}}! This is {{agent_name}} from {{company_name}}. I'm calling about your {{appointment_type}} appointment tomorrow at {{appointment_time}}."
    };
    setOverridesJson(JSON.stringify(example, null, 2));
    setTestCall({...testCall, overrides: example});
  };


  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Outbound API Management</h1>
            <p className="text-muted-foreground">Manage outbound PSTN calls via webhooks</p>
          </div>
        </div>

        {/* Tab Buttons */}
        <div className="flex gap-2 border-b border-border/60">
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-6 py-3 rounded-t-lg font-medium transition-all duration-300 flex items-center gap-2 ${
              activeTab === 'settings'
                ? 'bg-gradient-button text-primary-foreground shadow-lg shadow-primary/20'
                : 'text-muted-foreground hover:text-foreground border border-border/60 hover:border-primary/30 bg-transparent'
            }`}
          >
            <Settings className="w-4 h-4" />
            Settings
          </button>
          <button
            onClick={() => setActiveTab('test')}
            className={`px-6 py-3 rounded-t-lg font-medium transition-all duration-300 flex items-center gap-2 ${
              activeTab === 'test'
                ? 'bg-gradient-button text-primary-foreground shadow-lg shadow-primary/20'
                : 'text-muted-foreground hover:text-foreground border border-border/60 hover:border-primary/30 bg-transparent'
            }`}
          >
            <FlaskConical className="w-4 h-4" />
            Test
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 bg-success/20 border border-success/30 text-success px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}
      {error && (
        <div className="mb-4 bg-destructive/20 border border-destructive/30 text-destructive px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="flex-1 overflow-y-auto">
          <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <Key className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground">API Configuration</h3>
                <p className="text-sm text-muted-foreground">Configure your outbound API credentials and settings</p>
              </div>
            </div>
            
            <div className="space-y-6">
              {/* API Secret Management */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  API Secret
                </label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input
                      type={showApiSecret ? "text" : "password"}
                      value={apiSecret}
                      onChange={(e) => setApiSecret(e.target.value)}
                      placeholder="Your API secret will appear here"
                      className="w-full bg-muted/50 border border-border/60 rounded-lg px-4 py-3 pr-12 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300"
                      readOnly
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiSecret(!showApiSecret)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      title={showApiSecret ? "Hide secret" : "Show secret"}
                    >
                      {showApiSecret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <button
                    onClick={rotateApiSecret}
                    disabled={loading}
                    className="px-4 py-3 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center gap-2 font-medium btn-hover-gradient"
                  >
                    {loading ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4" />
                        Rotate
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      if (apiSecret) {
                        navigator.clipboard.writeText(apiSecret);
                        setSuccess('API secret copied to clipboard!');
                        setTimeout(() => setSuccess(''), 2000);
                      }
                    }}
                    disabled={!apiSecret}
                    className="px-4 py-3 bg-muted/50 hover:bg-muted border border-border/60 text-foreground rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center gap-2 font-medium"
                    title="Copy to clipboard"
                  >
                    <Copy className="w-4 h-4" />
                    Copy
                  </button>
                </div>
                <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Use this secret to sign your outbound API requests with HMAC-SHA256
                </p>
              </div>

              {/* API Documentation */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-5 h-5 text-primary" />
                  <h4 className="text-lg font-semibold text-foreground">API Endpoint</h4>
                </div>
                <div className="bg-muted/30 border border-border/60 rounded-lg p-4">
                  <code className="text-primary font-mono text-sm">POST https://api.braincxai.com/v1/outbound/calls/token</code>
                  <div className="mt-4 space-y-3">
                    <p className="text-sm font-medium text-foreground">Required Headers:</p>
                    <p className="text-sm text-muted-foreground">Authorization: Bearer "Secret Key" <apiSecret></apiSecret></p>
                  </div>
                </div>
              </div>

              {/* Request Example */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Code className="w-5 h-5 text-primary" />
                  <h4 className="text-lg font-semibold text-foreground">Request Example</h4>
                </div>
                <div className="bg-muted/30 border border-border/60 rounded-lg p-4">
                  <pre className="text-sm text-foreground overflow-x-auto font-mono">
{`{
  "from_number": "+12137771234",
  "to_number": "+12137771235",
  "agent_id": "optional-agent-id",
  "overrides": {
    "system_prompt": "Custom prompt",
    "voice_id": "custom-voice",
    "greeting_text": "Hello!"
  },
  "variables": {
    "customer_name": "John Doe",
    "appointment_time": "2:00 PM"
  },
  "metadata": {
    "campaign_id": "campaign-123"
  }
}`}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Test Tab */}
      {activeTab === 'test' && (
        <div className="flex-1 overflow-y-auto">
          <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <FlaskConical className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground">Test Outbound Call</h3>
                <p className="text-sm text-muted-foreground">Test your outbound API configuration</p>
              </div>
            </div>
            
            <div className="space-y-6">
              {/* Phone Numbers */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    From Number (Caller ID)
                  </label>
                  <div className="relative" ref={dropdownRef}>
                    <button
                      type="button"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className={`w-full bg-background border ${
                        isDropdownOpen 
                          ? 'border-primary shadow-lg shadow-primary/20' 
                          : 'border-primary/50 hover:border-primary hover:shadow-lg hover:shadow-primary/10'
                      } rounded-lg px-4 py-3 text-left text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300 flex items-center justify-between group`}
                    >
                      <span className={`${testCall.from_number ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {testCall.from_number 
                          ? phoneNumbers.find(p => p.e164 === testCall.from_number)?.e164 + 
                            (phoneNumbers.find(p => p.e164 === testCall.from_number)?.agent_id 
                              ? ` (${agents.find(a => a.id === phoneNumbers.find(p => p.e164 === testCall.from_number)?.agent_id)?.name || 'Unknown Agent'})` 
                              : '')
                          : `Select caller ID number (${phoneNumbers.length} available)`
                        }
                      </span>
                      <ChevronDown 
                        className={`w-4 h-4 text-primary transition-transform duration-300 ${
                          isDropdownOpen ? 'rotate-180' : ''
                        }`} 
                      />
                    </button>
                    
                    {/* Dropdown Menu */}
                    {isDropdownOpen && (
                      <div 
                        className="absolute z-50 w-full mt-2 bg-card/95 backdrop-blur-md border border-border/60 rounded-lg shadow-elegant overflow-hidden dropdown-open"
                      >
                        <div className="max-h-60 overflow-y-auto custom-scrollbar">
                          <button
                            type="button"
                            onClick={() => {
                              setTestCall({...testCall, from_number: ''});
                              setIsDropdownOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-left text-sm transition-all duration-200 hover:bg-primary/10 hover:text-primary flex items-center gap-2 dropdown-item ${
                              !testCall.from_number 
                                ? 'bg-primary/10 text-primary' 
                                : 'text-foreground'
                            }`}
                            style={{
                              animationDelay: '0s'
                            }}
                          >
                            <Phone className="w-4 h-4" />
                            Select caller ID number ({phoneNumbers.length} available)
                          </button>
                          {phoneNumbers.map((phone, index) => {
                            const agentName = phone.agent_id 
                              ? agents.find(a => a.id === phone.agent_id)?.name || 'Unknown Agent'
                              : null;
                            const isSelected = testCall.from_number === phone.e164;
                            return (
                              <button
                                key={phone.id}
                                type="button"
                                onClick={() => {
                                  setTestCall({...testCall, from_number: phone.e164});
                                  setIsDropdownOpen(false);
                                }}
                                className={`w-full px-4 py-3 text-left text-sm transition-all duration-200 hover:bg-primary/10 hover:text-primary flex items-center justify-between group dropdown-item ${
                                  isSelected 
                                    ? 'bg-primary/10 text-primary' 
                                    : 'text-foreground'
                                }`}
                                style={{
                                  animationDelay: `${(index + 1) * 0.02}s`
                                }}
                              >
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  {isSelected && <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />}
                                  <span className="font-mono">{phone.e164}</span>
                                  {agentName && (
                                    <span className="text-muted-foreground text-xs truncate">
                                      ({agentName})
                                    </span>
                                  )}
                                </div>
                                {isSelected && (
                                  <div className="w-2 h-2 rounded-full bg-primary animate-pulse ml-2 flex-shrink-0" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                    <PhoneCall className="w-4 h-4" />
                    To Number (Target)
                  </label>
                  <input
                    type="tel"
                    value={testCall.to_number}
                    onChange={(e) => setTestCall({...testCall, to_number: e.target.value})}
                    placeholder="+12137771235"
                    className="w-full bg-muted/50 border border-border/60 rounded-lg px-4 py-3 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300"
                  />
                </div>
              </div>

              {/* JSON Editors */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-foreground flex items-center gap-2">
                      <Code className="w-4 h-4" />
                      Variables (JSON)
                    </label>
                    <button
                      onClick={setExampleVariables}
                      className="text-xs px-3 py-1.5 bg-primary/20 hover:bg-primary/30 text-primary border border-primary/30 rounded-lg transition-all duration-300 flex items-center gap-1 font-medium btn-hover-secondary"
                    >
                      <Zap className="w-3 h-3" />
                      Load Example
                    </button>
                  </div>
                  <textarea
                    value={variablesJson}
                    onChange={(e) => {
                      setVariablesJson(e.target.value);
                      try {
                        const parsed = JSON.parse(e.target.value);
                        setTestCall({...testCall, variables: parsed});
                      } catch (err) {
                        // Invalid JSON, but keep the text for editing
                      }
                    }}
                    placeholder='{"first_name": "John", "appointment_time": "2:00 PM"}'
                    rows={8}
                    className="w-full bg-muted/30 border border-border/60 rounded-lg px-4 py-3 text-foreground placeholder-muted-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 resize-y"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-foreground flex items-center gap-2">
                      <Code className="w-4 h-4" />
                      Overrides (JSON)
                    </label>
                    <button
                      onClick={setExampleOverrides}
                      className="text-xs px-3 py-1.5 bg-primary/20 hover:bg-primary/30 text-primary border border-primary/30 rounded-lg transition-all duration-300 flex items-center gap-1 font-medium btn-hover-secondary"
                    >
                      <Zap className="w-3 h-3" />
                      Load Example
                    </button>
                  </div>
                  <textarea
                    value={overridesJson}
                    onChange={(e) => {
                      setOverridesJson(e.target.value);
                      try {
                        const parsed = JSON.parse(e.target.value);
                        setTestCall({...testCall, overrides: parsed});
                      } catch (err) {
                        // Invalid JSON, but keep the text for editing
                      }
                    }}
                    placeholder='{"system_prompt": "You are calling {{customer_name}} about their appointment at {{appointment_time}}.", "greeting_text": "Hello {{customer_name}}!"}'
                    rows={8}
                    className="w-full bg-muted/30 border border-border/60 rounded-lg px-4 py-3 text-foreground placeholder-muted-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 resize-y"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-border/50">
                <button
                  onClick={testOutboundCall}
                  disabled={loading || !testCall.from_number || !testCall.to_number}
                  className="px-6 py-3 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center gap-2 font-medium"
                >
                  {loading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      Creating Test Call...
                    </>
                  ) : (
                    <>
                      <PhoneCall className="w-4 h-4" />
                      Create Test Call
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    const request = generateTestRequest();
                    navigator.clipboard.writeText(JSON.stringify(request, null, 2));
                    setSuccess('Request copied to clipboard!');
                    setTimeout(() => setSuccess(''), 2000);
                  }}
                  className="px-4 py-3 bg-muted/50 hover:bg-muted border border-border/60 text-foreground rounded-lg transition-all duration-300 flex items-center gap-2 font-medium btn-hover-secondary"
                >
                  <Copy className="w-4 h-4" />
                  Copy Request
                </button>
                <button
                  onClick={() => {
                    if (phoneNumbers.length > 0) {
                      setTestCall({...testCall, from_number: phoneNumbers[0].e164});
                      setSuccess('From number auto-filled!');
                      setTimeout(() => setSuccess(''), 2000);
                    }
                  }}
                  className="px-4 py-3 bg-primary/20 hover:bg-primary/30 text-primary border border-primary/30 rounded-lg transition-all duration-300 flex items-center gap-2 font-medium btn-hover-secondary"
                >
                  <Zap className="w-4 h-4" />
                  Auto-fill From
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutboundApiManager;
